import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { FaBan, FaCheck, FaPencilAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  listarSociosPrestadorPj,
  salvarSociosPrestadorPj,
} from '~/Services/Service';
import { format } from '~/Utils/Helpers';
import ModalAddSocio from './ModalAddSocio';

const paraCamel = socio => ({
  nome: socio.nome,
  cpf: socio.cpf,
  percentualParticipacao: socio.percentual_participacao,
});

const somarPercentuais = lista =>
  Math.round(lista.reduce((t, s) => t + Number(s.percentualParticipacao || 0), 0) * 100) / 100;

const normalizarPercentual = value => {
  let valor = (value || '').replace(',', '.').replace(/[^0-9.]/g, '');
  const partes = valor.split('.');

  if (partes.length > 2) {
    valor = `${partes[0]}.${partes.slice(1).join('')}`;
  }

  const [inteiro, decimal] = valor.split('.');
  const inteiroLimitado = (inteiro || '').slice(0, 3);
  const decimalLimitado = decimal !== undefined ? decimal.slice(0, 2) : undefined;
  const percentual = decimalLimitado !== undefined
    ? `${inteiroLimitado}.${decimalLimitado}`
    : inteiroLimitado;

  if (Number(percentual) > 100) return '100';
  return percentual;
};

const TabSocios = ({ codigoPj }) => {
  const [socios, setSocios] = useState([]);
  const [modal, setModal] = useState(false);
  const [socioEdicao, setSocioEdicao] = useState(null);
  const [ajusteSocios, setAjusteSocios] = useState([]);
  const [modalAjuste, setModalAjuste] = useState(false);

  const carregar = () => {
    listarSociosPrestadorPj(codigoPj)
      .then(resultado => setSocios(Array.isArray(resultado) ? resultado : []))
      .catch(err => toast.error(err.message || 'Erro ao carregar sócios.'));
  };

  useEffect(() => {
    if (codigoPj) carregar();
  }, [codigoPj]);

  const ativos = socios.filter(s => s.status === 'A').map(paraCamel);

  const persistirLista = async lista => {
    try {
      await salvarSociosPrestadorPj({
        codigo_pj: codigoPj,
        socios: lista.map(s => ({
          nome: s.nome,
          cpf: s.cpf,
          percentual_participacao: Number(s.percentualParticipacao),
        })),
      });
      toast.success('Sócios atualizados.');
      carregar();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar sócios.');
    }
  };

  const abrirAjuste = lista => {
    setAjusteSocios(lista);
    setModalAjuste(true);
  };

  const salvarLista = lista => {
    const soma = somarPercentuais(lista);
    if (lista.length >= 2 && soma !== 100) {
      abrirAjuste(lista);
      return;
    }

    persistirLista(lista);
  };

  const abrirAdicionar = () => {
    setSocioEdicao(null);
    setModal(true);
  };

  const abrirEdicao = socio => {
    setSocioEdicao(paraCamel(socio));
    setModal(true);
  };

  const fecharModal = () => {
    setModal(false);
    setSocioEdicao(null);
  };

  const confirmarModal = socio => {
    if (socioEdicao) {
      const cpfOriginal = socioEdicao.cpf;
      salvarLista(ativos.map(s => (s.cpf === cpfOriginal ? socio : s)));
    } else {
      salvarLista([...ativos.filter(s => s.cpf !== socio.cpf), socio]);
    }
  };

  const inativar = cpf => {
    persistirLista(ativos.filter(s => s.cpf !== cpf));
  };

  const ativar = socio => {
    salvarLista([
      ...ativos.filter(s => s.cpf !== socio.cpf),
      {
        nome: socio.nome,
        cpf: socio.cpf,
        percentualParticipacao: 0.01,
      },
    ]);
  };

  const alterarPercentualAjuste = (cpf, value) => {
    setAjusteSocios(atuais => atuais.map(socio => (
      socio.cpf === cpf
        ? { ...socio, percentualParticipacao: normalizarPercentual(value) }
        : socio
    )));
  };

  const confirmarAjuste = () => {
    const soma = somarPercentuais(ajusteSocios);
    const invalido = ajusteSocios.some(socio => (
      !socio.percentualParticipacao
      || Number(socio.percentualParticipacao) <= 0
      || Number(socio.percentualParticipacao) > 100
    ));

    if (invalido) {
      toast.error('Todos os percentuais devem estar entre 0,01 e 100.');
      return;
    }

    if (soma !== 100) {
      toast.error(`A soma dos percentuais deve ser 100% (atual: ${soma.toFixed(2)}%).`);
      return;
    }

    setModalAjuste(false);
    persistirLista(ajusteSocios);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">Soma ativa: {somarPercentuais(ativos).toFixed(2)}%</span>
        <Button className="hpj-btn" size="sm" variant="outline-secondary" onClick={abrirAdicionar}>
          Adicionar sócio
        </Button>
      </div>
      <div className="hpj-card hpj-card--overflow d-none d-md-block">
        <Table className="hpj-table" responsive>
          <thead>
            <tr>
              <th>Nome</th>
              <th>CPF</th>
              <th>Participação</th>
              <th>Status</th>
              <th className="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {socios.map(socio => (
              <tr key={`${socio.codigo}-${socio.cpf}`}>
                <td className="hpj-main-text">{socio.nome}</td>
                <td>{format.cpf(socio.cpf)}</td>
                <td>{Number(socio.percentual_participacao).toFixed(2)}%</td>
                <td>
                  <span className={`hpj-badge ${socio.status === 'A' ? 'hpj-badge--green' : 'hpj-badge--slate'}`}>
                    {socio.status === 'A' ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="text-center">
                  <div className="d-flex justify-content-center" style={{ gap: '0.5rem' }}>
                    {socio.status === 'A' ? (
                      <>
                      <Button
                        className="hpj-btn"
                        size="sm"
                        variant="outline-primary"
                        title="Editar sócio"
                        onClick={() => abrirEdicao(socio)}
                      >
                        <FaPencilAlt />
                      </Button>
                      <Button
                        className="hpj-btn"
                        size="sm"
                        variant="outline-danger"
                        title="Inativar sócio"
                        onClick={() => inativar(socio.cpf)}
                      >
                        <FaBan />
                      </Button>
                      </>
                    ) : (
                      <Button
                        className="hpj-btn"
                        size="sm"
                        variant="outline-success"
                        title="Ativar sócio"
                        onClick={() => ativar(socio)}
                      >
                        <FaCheck />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!socios.length && (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  Nenhum sócio cadastrado
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-md-none hpj-tab-mobile-list">
        {socios.map(socio => (
          <div className="hpj-mobile-card" key={`${socio.codigo}-${socio.cpf}`}>
            <div className="hpj-mobile-card-head">
              <div className="hpj-mobile-title-block">
                <div className="hpj-mobile-title">{socio.nome}</div>
                <div className="hpj-mobile-subtitle">{format.cpf(socio.cpf)}</div>
              </div>
              <span className={`hpj-badge ${socio.status === 'A' ? 'hpj-badge--green' : 'hpj-badge--slate'}`}>
                {socio.status === 'A' ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="hpj-mobile-grid">
              <div className="hpj-mobile-field">
                <span>Participação</span>
                <strong>{Number(socio.percentual_participacao).toFixed(2)}%</strong>
              </div>
              <div className="hpj-mobile-field">
                <span>Status</span>
                <strong>{socio.status === 'A' ? 'Ativo' : 'Inativo'}</strong>
              </div>
            </div>
            <div className="hpj-mobile-actions">
              {socio.status === 'A' ? (
                <>
                  <Button
                    className="hpj-mobile-action-btn"
                    size="sm"
                    variant="outline-primary"
                    onClick={() => abrirEdicao(socio)}
                  >
                    <FaPencilAlt className="mr-1" /> Editar
                  </Button>
                  <Button
                    className="hpj-mobile-action-btn hpj-mobile-action-btn--danger"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => inativar(socio.cpf)}
                  >
                    <FaBan className="mr-1" /> Inativar
                  </Button>
                </>
              ) : (
                <Button
                  className="hpj-mobile-action-btn hpj-mobile-action-btn--success"
                  size="sm"
                  variant="outline-success"
                  onClick={() => ativar(socio)}
                >
                  <FaCheck className="mr-1" /> Ativar
                </Button>
              )}
            </div>
          </div>
        ))}
        {!socios.length && (
          <div className="hpj-mobile-empty">
            Nenhum sócio cadastrado
          </div>
        )}
      </div>
      <ModalAddSocio
        show={modal}
        onHide={fecharModal}
        onAdicionar={confirmarModal}
        socio={socioEdicao}
      />
      <Modal className="hpj-modal hpj-modal-socio" show={modalAjuste} onHide={() => setModalAjuste(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Ajustar participação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="hpj-modal-helper mb-3">
            A soma dos percentuais deve fechar 100% para dois ou mais sócios ativos.
          </div>
          {ajusteSocios.map(socio => (
            <Row key={socio.cpf} className="align-items-end">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Nome</Form.Label>
                  <Form.Control value={socio.nome} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>%</Form.Label>
                  <Form.Control
                    inputMode="decimal"
                    max="100"
                    placeholder="0,00"
                    value={socio.percentualParticipacao}
                    onChange={event => alterarPercentualAjuste(socio.cpf, event.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          ))}
          <div className="hpj-modal-helper">
            Soma atual: {somarPercentuais(ajusteSocios).toFixed(2)}%
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="hpj-btn" variant="outline-secondary" onClick={() => setModalAjuste(false)}>
            Cancelar
          </Button>
          <Button className="hpj-btn hpj-btn-primary" variant="primary" onClick={confirmarAjuste}>
            Salvar ajuste
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TabSocios;
